import { useEffect } from 'react';

import { AUTH_LOGGED_IN_PARAMETER } from '../config';
import { useUserTrackingLazyQuery } from './__graphql__/tracking';
import { persistedDataLayer, pushDataEvent } from './dataLayer';

export { useUserTracking };

type DataEvent = Record<string, unknown>;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    appboy: any; // This is a third party.
    dataLayer: DataEvent[];
  }
}

function useUserTracking() {
  const [getUser, { data }] = useUserTrackingLazyQuery();

  useEffect(() => {
    persistedDataLayer.init();

    const url = new URL(window.location.href);
    const isFirstLogin = url?.searchParams.has(AUTH_LOGGED_IN_PARAMETER);
    const hasEcommerceStatus = window.dataLayer?.some(
      (event) => event.eventAction === 'EcommerceStatus'
    );
    const shouldFetchUser = isFirstLogin || !hasEcommerceStatus;

    if (!shouldFetchUser) return;

    getUser();

    if (data?.userV2?.__typename !== 'AuthenticatedUser') return;

    if (!hasEcommerceStatus) {
      const { ecommerce_status } = data.userV2;

      pushDataEvent(
        {
          event: 'analyticsEvent',
          eventCategory: 'Auth',
          eventAction: 'EcommerceStatus',
          eventLabel: ecommerce_status,
        },
        { persist: true }
      );
    }

    if (isFirstLogin) {
      const { id, native_id, email, first_name, last_name } = data.userV2;

      url.searchParams.delete(AUTH_LOGGED_IN_PARAMETER);
      window.history.replaceState(null, '', url.toString());

      pushDataEvent(
        {
          event: 'analyticsEvent',
          eventCategory: 'Auth',
          eventAction: 'Identification',
          eventLabel: native_id,
        },
        { persist: true }
      );

      pushToBraze({
        id,
        firstName: first_name || 'unknown',
        lastName: last_name || 'unknown',
        email,
      });
    }
  }, [data, getUser]);
}

function pushToBraze({
  id,
  email,
  firstName,
  lastName,
}: {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}) {
  if (typeof window.appboy !== 'undefined') {
    window.appboy.changeUser(id);
    window.appboy.getUser().setEmail(email);
    window.appboy.getUser().setFirstName(firstName);
    window.appboy.getUser().setLastName(lastName);
  }
}
